import { ProfileViewSource } from '@up2stay/up2promo-api-client';
import {
  Button,
  CopyButton,
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  QRCode,
  Separator,
  Skeleton,
  cn,
  qrWellSurfaceClass,
} from '@up2stay/up2promo-ui';
import {
  CheckCircle2,
  Link as LinkIcon,
  RefreshCw,
  Share2,
  Wallet,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { useAuthPublicConfig } from '@/core/hooks/use-auth-public-config';
import { useLocale } from '@/core/hooks/use-locale';
import { useProfile } from '@/core/hooks/use-profile';
import { useWalletCard } from '@/core/hooks/use-wallet-card';
import { triggerTapHaptic } from '@/core/lib/dnd-haptics';
import { addProfileToWallet } from '@/core/lib/wallet-card';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { useAuthStore } from '@/core/store/auth.store';
import { useCurrentWorkspace } from '@/core/store/workspace.store';
import { getApiErrorMessage } from '@/core/utils/api-error.utils';
import { buildPublicProfileHashUrl } from '@/core/utils/product-link.utils';
import { buildProfileUrl } from '@/core/utils/profile.utils';

type QRCodeDrawerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function QRCodeDrawer({ open, onOpenChange }: QRCodeDrawerProps) {
  const dict = useDictionary();
  const locale = useLocale();
  const user = useAuthStore((state) => state.user);
  const currentWorkspace = useCurrentWorkspace();
  const { walletEnabled } = useAuthPublicConfig();
  const { profile, isLoading: profileLoading } = useProfile();
  const {
    state: walletCardState,
    refresh: refreshWalletCard,
    watchForChange: watchWalletCard,
  } = useWalletCard(profile?.id);
  const [_copied, setCopied] = useState(false);
  const [isAddingToWallet, setIsAddingToWallet] = useState(false);

  const workspaceUsername = currentWorkspace?.username || null;

  // Direct profile URL (for sharing - always use direct profile viewer URL)
  const profileUrl = useMemo(() => {
    return buildProfileUrl({
      profile,
      username: user?.username,
      locale,
      workspaceUsername: workspaceUsername,
    });
  }, [profile, user?.username, locale, workspaceUsername]);

  // QR code encodes only the stable publicHash URL (never direct profile URL).
  const qrCodeUrl = useMemo(() => {
    if (profile?.publicHash) {
      return buildPublicProfileHashUrl(
        profile.publicHash,
        ProfileViewSource.PROFILE_QR,
      );
    }
    return null;
  }, [profile]);

  const isLoading = profileLoading;

  useEffect(() => {
    if (open) {
      refreshWalletCard();
    }
  }, [open, refreshWalletCard]);

  const walletButton = useMemo(() => {
    const shareDict = dict.profiles.share as {
      addToWallet?: string;
      walletUpdate?: string;
      walletUpToDate?: string;
      walletOutdatedHint?: string;
    };

    if (walletCardState === 'outdated') {
      return {
        label: shareDict.walletUpdate || '',
        hint: shareDict.walletOutdatedHint || '',
      };
    }
    if (walletCardState === 'current') {
      return { label: shareDict.walletUpToDate || '', hint: null };
    }
    return { label: shareDict.addToWallet || '', hint: null };
  }, [walletCardState, dict.profiles.share]);

  const handleAddToWallet = useCallback(async () => {
    if (!profile || !qrCodeUrl || isAddingToWallet) return;

    const shareDict = dict.profiles.share as {
      defaultProfileName?: string;
      walletAddFailed?: string;
    };

    setIsAddingToWallet(true);
    try {
      await addProfileToWallet({
        profileId: profile.id,
        fileName: `${profile.title || shareDict.defaultProfileName || 'profile'}.pkpass`,
      });
      // The device registers itself with Apple only after the user confirms,
      // so keep polling until that registration shows up.
      watchWalletCard();
    } catch (error) {
      toast.error(
        getApiErrorMessage(error, shareDict.walletAddFailed || '') ||
          shareDict.walletAddFailed ||
          '',
      );
    } finally {
      setIsAddingToWallet(false);
    }
  }, [
    profile,
    qrCodeUrl,
    isAddingToWallet,
    dict.profiles.share,
    watchWalletCard,
  ]);

  const handleShareProfile = async () => {
    // Always use direct profile URL for sharing (not the product URL)
    if (!profileUrl) return;

    // For AirDrop compatibility, share just the URL without any text
    // Try native share API first (works on mobile)
    if (navigator.share) {
      try {
        await navigator.share({
          url: profileUrl, // Just the URL for AirDrop compatibility
        });
        return;
      } catch (err) {
        // User cancelled or share failed, fall back to copy
        if ((err as Error).name !== 'AbortError') {
          console.error('Share failed:', err);
        }
      }
    }

    // Fallback to copy if native share is not available
    await navigator.clipboard.writeText(profileUrl);
  };

  return (
    <Drawer
      open={open}
      onOpenChange={(next) => {
        void triggerTapHaptic();
        onOpenChange(next);
      }}
    >
      <DrawerContent scrollable={false}>
        <DrawerHeader>
          <DrawerTitle className="text-base sm:text-lg">
            {dict.nav.showMyQrCode}
          </DrawerTitle>
          <DrawerDescription className="text-xs sm:text-sm">
            {dict.nav.showMyQrCodeDescription}
          </DrawerDescription>
        </DrawerHeader>

        <div className="mt-4 sm:mt-6 space-y-4 sm:space-y-6">
          {/* QR Code Display */}
          <div className="flex flex-col items-center gap-3 sm:gap-4">
            {isLoading ? (
              <div
                className={cn(
                  'flex h-40 w-40 items-center justify-center rounded-lg sm:h-48 sm:w-48',
                  qrWellSurfaceClass,
                )}
              >
                <Skeleton className="h-full w-full" />
              </div>
            ) : qrCodeUrl ? (
              <div
                className={cn(
                  'flex h-40 w-40 flex-shrink-0 items-center justify-center rounded-lg p-3 sm:h-48 sm:w-48 sm:p-4',
                  qrWellSurfaceClass,
                )}
              >
                <QRCode
                  data={qrCodeUrl}
                  className="size-full [&_svg]:bg-transparent"
                />
              </div>
            ) : (
              <div
                className={cn(
                  'flex h-40 w-40 items-center justify-center rounded-lg p-3 sm:h-48 sm:w-48 sm:p-4',
                  qrWellSurfaceClass,
                )}
              >
                <div className="space-y-2 px-2 text-center">
                  <div className="text-xs text-muted-foreground sm:text-sm">
                    {dict.nav.showMyQrCodeDescription}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {!profile
                      ? dict.profiles.noProfileYet
                      : !user?.username
                        ? dict.profiles.share.noUsernameHint
                        : dict.nav.showMyQrCodeDescription}
                  </div>
                </div>
              </div>
            )}
          </div>

          <Separator />

          {/* Action Buttons */}
          <div className="space-y-2 sm:space-y-3">
            <Button
              variant="outline"
              className="w-full justify-start gap-2 sm:gap-3 h-auto py-3 sm:py-4 px-3 sm:px-4"
              disabled={!profileUrl}
            >
              <div className="flex h-9 w-9 sm:h-10 sm:w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10">
                <LinkIcon className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
              </div>
              <div className="flex flex-col items-start min-w-0 flex-1 text-left">
                <span className="font-medium text-sm sm:text-base truncate w-full">
                  {dict.profiles.share.profileLink || dict.nav.copyLink}
                </span>
                <span className="text-xs sm:text-sm text-muted-foreground break-words whitespace-normal w-full leading-relaxed">
                  {dict.nav.copyLinkDescription}
                </span>
              </div>
              {profileUrl ? (
                <CopyButton
                  content={profileUrl}
                  aria-label={dict.nav.copyLink}
                  variant="outline"
                  size="md"
                  className="flex-shrink-0"
                  onCopy={() => void triggerTapHaptic()}
                  onCopyChange={setCopied}
                />
              ) : null}
            </Button>

            <Button
              variant="outline"
              className="w-full justify-start gap-2 sm:gap-3 h-auto py-3 sm:py-4 px-3 sm:px-4"
              onClick={handleShareProfile}
              disabled={!profileUrl}
            >
              <div className="flex h-9 w-9 sm:h-10 sm:w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10">
                <Share2 className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
              </div>
              <div className="flex flex-col items-start min-w-0 flex-1 text-left">
                <span className="font-medium text-sm sm:text-base truncate w-full">
                  {dict.nav.shareProfile}
                </span>
                <span className="text-xs sm:text-sm text-muted-foreground break-words whitespace-normal w-full leading-relaxed">
                  {dict.nav.shareProfileDescription}
                </span>
              </div>
            </Button>

            {walletEnabled ? (
              <Button
                variant="outline"
                className={cn(
                  'w-full justify-start gap-2 sm:gap-3 h-auto py-3 sm:py-4 px-3 sm:px-4',
                  walletCardState === 'outdated' &&
                    'border-amber-500/60 text-amber-700 dark:text-amber-400',
                )}
                onClick={() => {
                  void triggerTapHaptic();
                  void handleAddToWallet();
                }}
                disabled={!profile || !qrCodeUrl || isAddingToWallet}
              >
                <div className="flex h-9 w-9 sm:h-10 sm:w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10">
                  {walletCardState === 'outdated' ? (
                    <RefreshCw className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                  ) : walletCardState === 'current' ? (
                    <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                  ) : (
                    <Wallet className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                  )}
                </div>
                <div className="flex flex-col items-start min-w-0 flex-1 text-left">
                  <span className="font-medium text-sm sm:text-base truncate w-full">
                    {walletButton.label}
                  </span>
                  {walletButton.hint ? (
                    <span className="text-xs sm:text-sm text-muted-foreground break-words whitespace-normal w-full leading-relaxed">
                      {walletButton.hint}
                    </span>
                  ) : null}
                </div>
              </Button>
            ) : null}
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
